<script lang="ts">
import { templateRef, useElementSize, useWindowSize } from '@vueuse/core'
import type { InjectionKey, Ref } from 'vue'
import { defineComponent, inject, provide, ref } from 'vue'

import { countryInjectionKey } from '@/App.vue'
import AdpLogo from '@/assets/images/adp-logo.svg'
import { useBreakpoint } from '@/functions/useBreakpoint'
import { useHostURL } from '@/functions/useHostURL'
import { useSmoothScroll } from '@/functions/useSmoothScroll'
import ButtonIcon from '@/molecules/ButtonIcon.vue'
import ButtonSound from '@/molecules/ButtonSound.vue'
import LanguageSwitcher from '@/organisms/LanguageSwitcher.vue'
import { Theme } from '@/types'
import { useAudio } from '@/utils/audio'

export type PageLayoutPublicInstance = {
  footerHeight: Ref<number>
  headerHeight: Ref<number>
  hideUI: Ref<boolean>
  paperHeader: Ref<boolean>
  theme: Ref<`${Theme}`>
  resetScroll: () => void
  scrollTo: (target: unknown, animate?: boolean) => void,
  hideHeader: Ref<boolean>
}

export const pageLayoutInjectionKey: InjectionKey<PageLayoutPublicInstance> = Symbol('page-layout')

export default defineComponent({
  name: 'PageLayout',
  components: {
    AdpLogo,
    ButtonIcon,
    ButtonSound,
    LanguageSwitcher
  },
  setup () {
    const breakpoint = useBreakpoint()
    const audio = useAudio('click')
    const theme = ref<`${Theme}`>(Theme.DEFAULT)
    const header = templateRef<HTMLElement>('header')
    const footer = templateRef<HTMLElement>('footer')
    const { height: headerHeight } = useElementSize(header, undefined, { box: 'border-box' })
    const { height: footerHeight } = useElementSize(footer, undefined, { box: 'border-box' })
    const hideUI = ref(false)
    const hideHeader = ref(false)
    const paperHeader = ref(false)
    const country = inject(countryInjectionKey)
    const { cart: cartURL, home: homeURL, login: loginURL } = useHostURL()
    const { resetScroll, scrollTo } = useSmoothScroll()
    const { width: windowWidth } = useWindowSize()

    provide(pageLayoutInjectionKey, {
      footerHeight,
      headerHeight,
      hideUI,
      paperHeader,
      theme,
      resetScroll,
      scrollTo,
      hideHeader
    })

    return {
      audio,
      breakpoint,
      cartURL,
      country,
      hideUI,
      homeURL,
      loginURL,
      hideHeader,
      paperHeader,
      theme,
      windowWidth
    }
  }
})
</script>

<template>
  <div class="page" :data-theme="theme">
    <header
      ref="header"
      class="w-full p-4 fixed top-0 left-0 z-3 text-center pointer-events-none"
      :class="{ 'paper shadow': paperHeader, 'hidden': hideHeader}"
      :style="{ width: `${windowWidth}px` }"
    >
      <a :href="homeURL" target="_blank" class="relative z-3">
        <AdpLogo class="w-60 mx-auto" />
      </a>
    </header>
    <main class="relative">
      <slot />
    </main>
    <Transition>
      <footer v-if="!hideUI" ref="footer" class="flex w-full p-4 fixed bottom-0 left-0 z-2 items-center justify-between pointer-events-none">
        <nav class="flex items-center">
          <ButtonSound />
          <LanguageSwitcher v-if="breakpoint.md" class="ml-4" />
        </nav>
        <slot name="footer" />
        <nav class="flex items-center">
          <ButtonIcon
            v-if="loginURL && breakpoint.md"
            icon="user"
            :href="loginURL"
            target="_parent"
            data-track-cta-name="Login"
            @click="audio.play()"
          />
          <ButtonIcon
            v-if="cartURL"
            class="ml-4"
            icon="cart"
            :href="cartURL"
            target="_parent"
            data-track-cta-name="Cart"
            @click="audio.play()"
          />
        </nav>
      </footer>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.page {
  &[data-theme="dark"] {
    color: $c-white;
  }

  &[data-theme="light"] {
    color: $c-black;
  }
}

header,
footer {
  & > * {
    pointer-events: auto;
  }
}

header {
  z-index: $z-header;
}

main {
  min-height: 100vh;
  min-height: 100lvh;
}

footer {
  z-index: $z-footer;
}

.logo > svg {
  transition: color $default-duration;

  [data-theme="dark"] & {
    &:first-child {
      color: $c-white;
    }

    &:last-child {
      color: $c-black;
    }
  }

  [data-theme="light"] & { // stylelint-disable-line
    &:first-child {
      color: $c-black;
    }

    &:last-child {
      color: $c-white;
    }
  }
}
</style>
